import { resolveBackend } from "../storage/resolveBackend";
import type { PersistedSliceDescriptor } from "../types";
import { buildEnvelope, scheduleSliceWrite } from "./debouncedWriter";
import { decodeEnvelope } from "./decodeEnvelope";
import { persistenceMemoryStore } from "./persistenceMemoryStore";
import {
    clearRetainedFuturePayload,
    retainFuturePayload,
} from "./unknownPayloadKeeper";

/**
 * Per-slice hydration metadata (values live in {@link persistenceMemoryStore}).
 */
type PersistedSliceMeta = {

    /** Whether `hydrate` has completed at least once for this key. */
    "hydrated": boolean;
};

/**
 * Coordinates disk hydration, debounced writes, and in-memory subscriptions for persisted slices.
 */
class PersistentSliceStoreClass {

    private readonly metaByKey = new Map<string, PersistedSliceMeta>();

    private bootstrappedEager = false;

    /**
     * Returns metadata for a slice key, creating a default entry when missing.
     *
     * @param sliceKey Slice storage key.
     * @returns Hydration metadata.
     */
    private getOrCreateMeta( sliceKey: string ): PersistedSliceMeta {
        let meta = this.metaByKey.get( sliceKey );
        if ( meta === undefined ) {
            meta = { "hydrated": false };
            this.metaByKey.set( sliceKey, meta );
        }

        return meta;
    }

    /**
     * Hydrates all eager slices during application boot.
     *
     * @param eagerSlices Slice descriptors listed in `EAGER_PERSISTED_SLICES`.
     */
    async hydrateEagerSlices( eagerSlices: readonly PersistedSliceDescriptor<unknown>[] ): Promise<void> {
        await Promise.all( eagerSlices.map( ( slice ) => this.hydrate( slice ) ) );
        this.bootstrappedEager = true;
    }

    /**
     * Whether eager hydration has completed.
     *
     * @returns `true` after `hydrateEagerSlices` finishes.
     */
    isBootReady(): boolean {
        return this.bootstrappedEager;
    }

    /**
     * Ensures a slice is loaded from storage before reads (lazy slices).
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     */
    async ensureHydrated<TValue>( slice: PersistedSliceDescriptor<TValue> ): Promise<void> {
        const meta = this.getOrCreateMeta( slice.key );
        if ( meta.hydrated ) {
            return;
        }

        await this.hydrate( slice );
    }

    /**
     * Loads a slice from its backend and populates the in-memory cache.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     */
    async hydrate<TValue>( slice: PersistedSliceDescriptor<TValue> ): Promise<void> {
        const meta = this.getOrCreateMeta( slice.key );
        if ( meta.hydrated ) {
            return;
        }

        const backend = resolveBackend( slice.storage );
        const raw = await backend.get<unknown>( slice.key );
        const decoded = decodeEnvelope( slice, raw );

        persistenceMemoryStore.replaceValue( slice, decoded.value );
        meta.hydrated = true;

        if ( decoded.isFromFuturePayload && raw !== null ) {
            retainFuturePayload( slice.key, raw );
        }
    }

    /**
     * Returns the current in-memory snapshot for `useSyncExternalStore`.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @returns Current value (schema defaults when not yet hydrated).
     */
    getSnapshot<TValue>( slice: PersistedSliceDescriptor<TValue> ): TValue {
        return persistenceMemoryStore.getSnapshot( slice );
    }

    /**
     * Peeks at the current value without triggering hydration.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @returns Cached value or `slice.default()` when the entry does not exist.
     */
    peek<TValue>( slice: PersistedSliceDescriptor<TValue> ): TValue {
        return persistenceMemoryStore.peek( slice );
    }

    /**
     * Subscribes to slice updates for `useSyncExternalStore`.
     *
     * @param sliceKey Slice storage key.
     * @param listener Change listener invoked after `setValue` or `hydrate`.
     * @returns Unsubscribe function.
     */
    subscribe<TValue>( slice: PersistedSliceDescriptor<TValue>, listener: () => void ): () => void {
        return persistenceMemoryStore.subscribe( slice, listener );
    }

    /**
     * Updates a slice value and schedules persistence.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @param next New value or functional updater (same semantics as `useState`).
     */
    setValue<TValue>(
        slice: PersistedSliceDescriptor<TValue>,
        next: TValue | ( ( previous: TValue ) => TValue ),
    ): void {
        persistenceMemoryStore.setValue( slice, next );
        this.getOrCreateMeta( slice.key ).hydrated = true;
        clearRetainedFuturePayload( slice.key );

        const resolved = persistenceMemoryStore.peek( slice );
        const envelope = buildEnvelope( slice, resolved );
        scheduleSliceWrite( slice, envelope );
    }

    /**
     * Resets in-memory state and hydration metadata.
     *
     * @remarks Intended for unit tests only.
     */
    resetForTests(): void {
        persistenceMemoryStore.resetForTests();
        this.metaByKey.clear();
        this.bootstrappedEager = false;
    }

}

/** Shared persisted slice store used by `PersistenceProvider` and persistence hooks. */
export const persistentSliceStore = new PersistentSliceStoreClass();
